import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { FileText, Eye, EyeOff, UserPlus, LogIn } from 'lucide-react'
import { toast } from 'sonner'

export function AuthPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) return

    setLoading(true)
    try {
      if (isLogin) {
        // Sign in
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        })

        if (error) throw error

        toast.success('Inloggning lyckades!', {
          description: 'Du loggas in automatiskt.'
        })
      } else {
        // Sign up
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
          },
        })

        if (error) throw error

        toast.success('Registrering lyckades!', {
          description: 'Du kan nu logga in med dina uppgifter.'
        })
        
        // Switch to login mode after successful signup
        setIsLogin(true)
        setPassword('')
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Något gick fel. Försök igen.'
      toast.error(isLogin ? 'Fel vid inloggning' : 'Fel vid registrering', {
        description: message
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
            <FileText className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            Dokumentanalys
          </CardTitle>
          <CardDescription className="text-gray-600">
            Professionell dokumentgranskning med AI-stöd för revisorer och företag
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Login/Signup Toggle */}
          <div className="flex mb-6 bg-gray-100 p-1 rounded-lg">
            <Button
              type="button"
              variant={isLogin ? "default" : "ghost"}
              className="flex-1 text-sm"
              onClick={() => setIsLogin(true)}
            >
              <LogIn className="h-4 w-4 mr-2" />
              Logga in
            </Button>
            <Button
              type="button"
              variant={!isLogin ? "default" : "ghost"}
              className="flex-1 text-sm"
              onClick={() => setIsLogin(false)}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Registrera
            </Button>
          </div>

          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                E-postadress
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="din.epost@foretag.se"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Lösenord
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Ditt lösenord"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
              {!isLogin && (
                <p className="text-xs text-gray-500">
                  Lösenordet måste vara minst 6 tecken långt
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700" 
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {isLogin ? 'Loggar in...' : 'Registrerar...'}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {isLogin ? (
                    <>
                      <LogIn className="h-4 w-4" />
                      Logga in
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      Registrera konto
                    </>
                  )}
                </div>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Genom att {isLogin ? 'logga in' : 'registrera dig'} godkänner du våra användarvillkor och integritetspolicy
            </p>
          </div>

          {/* Organization registration link */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">
                Ny revisionsbyrå?
              </p>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => window.location.href = '/register-organization'}
              >
                <FileText className="h-4 w-4 mr-2" />
                Registrera ny revisionsbyrå
              </Button>
            </div>
          </div>

          {/* Features info */}
          <div className="mt-8 space-y-3">
            <h4 className="text-sm font-medium text-gray-900">Funktioner:</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                AI-driven dokumentanalys
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                Automatisk rapportgenerering
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                Säker ärendehantering
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                Teamsamarbete och rollhantering
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}